/**
 * Types for the DancerMedia service.
 *
 * Extracted verbatim from `dancer-media.service.ts` (behaviour-preserving
 * split). Re-exported from that module so existing import paths stay stable.
 */

export type MediaType = 'headshot' | 'photo' | 'video';

export interface DancerMedia {
  id: string;
  user_id: string | null;
  media_type: string;
  storage_path: string | null;
  external_url: string | null;
  title: string | null;
  description: string | null;
  display_order: number | null;
  is_primary: boolean | null;
  file_name: string | null;
  file_size_bytes: number | null;
  mime_type: string | null;
  meteor_id: string | null;
  professional_profile_id: string | null;
  width_px: number | null;
  height_px: number | null;
  created_at: string | null;
  updated_at: string | null;
  // Video residency signals (BAL-721 / video-playback-resolver). Present on
  // every `artist_media` row (`getMediaByUser` does `select('*')`), so the
  // render surfaces can route a migrated video to its live source instead of a
  // dead Supabase-Storage signed URL. Optional so non-video / legacy
  // constructors of this shape stay valid.
  provider?: string | null;
  mux_playback_id?: string | null;
  mux_status?: string | null;
  r2_key?: string | null;
  // Editorial bucket — Spec vocabulary backed by
  // `artist_media_category_check`. NULL or legacy values render in the
  // "Other" bucket on the public profile.
  category?: string | null;
  // Meteor cutover Module 5-E: rows whose underlying file is permanently
  // unavailable (1,781 rows across 519 dancers as of 2026-05-10). The
  // dancer-side editor filters these out and surfaces a re-upload banner
  // (commit 0ffc6d0d3 added consumers; this fixes the missing field).
  requires_reupload: boolean;
  lost_at: string | null;
  lost_reason: string | null;
}

export interface MediaUploadInput {
  mediaType: MediaType;
  title?: string;
  description?: string;
}

export interface VideoLinkInput {
  url: string;
  title?: string;
  description?: string;
}

/**
 * A 4:5 reframe crop chosen at upload, in the ORIGINAL image's intrinsic
 * pixel space, plus that image's intrinsic dimensions. Persisted on the
 * artist_media row (crop_x/y/w/h + width_px/height_px) and applied as CSS at
 * render — the file itself is never modified.
 */
export interface CropRectInput {
  x: number;
  y: number;
  w: number;
  h: number;
  width: number;
  height: number;
}

/** Locked hero aspect ratio (width / height) and the tolerance the finalize
 * validator allows around it (integer-rounded crop boxes never land exactly
 * on 0.8). */
export const CROP_ASPECT = 4 / 5;
export const CROP_ASPECT_TOLERANCE = 0.01;

/**
 * Validate a crop rect against its source image: the box must fall inside the
 * image bounds and hold the 4:5 ratio within tolerance (R4). Pure — callers
 * (the finalize service, unit tests) get a Result-shaped verdict, no throw.
 */
export function validateCropRect(
  crop: CropRectInput,
): { ok: true } | { ok: false; reason: string } {
  // Mirror the app-layer Zod schema AND the DB all-or-none CHECK so direct
  // service callers (the service is exported from @kit/artists) get the same
  // guarantees as the server action — defense in depth, no opaque DB errors.
  const members = [crop.x, crop.y, crop.w, crop.h, crop.width, crop.height];
  if (members.some((v) => !Number.isInteger(v))) {
    return { ok: false, reason: 'crop rect must be integer pixels' };
  }
  if (
    crop.x < 0 ||
    crop.y < 0 ||
    crop.w <= 0 ||
    crop.h <= 0 ||
    crop.width <= 0 ||
    crop.height <= 0
  ) {
    return { ok: false, reason: 'crop rect must be positive' };
  }
  if (crop.x + crop.w > crop.width || crop.y + crop.h > crop.height) {
    return { ok: false, reason: 'crop rect exceeds image bounds' };
  }
  const ratio = crop.w / crop.h;
  if (Math.abs(ratio - CROP_ASPECT) / CROP_ASPECT > CROP_ASPECT_TOLERANCE) {
    return { ok: false, reason: 'crop rect is not 4:5' };
  }
  return { ok: true };
}

/**
 * Input for finalizing a browser direct-upload (signed-URL flow): the file is
 * already in storage; this creates the canonical artist_media row.
 */
export interface FinalizeDirectUploadInput {
  mediaType: MediaType | 'document';
  storagePath: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  title?: string | null;
  description?: string | null;
  category?: string | null;
  /** Optional 4:5 reframe crop (headshot upload only). When present, both
   * intrinsic dimensions are required (enforced by the action schema); the
   * finalize service validates bounds + ratio before persisting. */
  crop?: CropRectInput | null;
}
